import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import { TailSpin } from "react-loader-spinner";
import "./billingV2.css";

const BILLING_API = "https://fbpsjefhv4.execute-api.us-east-1.amazonaws.com/dev/per-team-billing";
const AVG_OTPS_API = "https://fbpsjefhv4.execute-api.us-east-1.amazonaws.com/dev/all-otps-day-wise";

const BillingV2 = () => {
  const currentDate = new Date();
  const startOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );

  const [startDate, setStartDate] = useState(startOfMonth);
  const [endDate, setEndDate] = useState(currentDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");

  const [initialTotalValidOtps, setInitialTotalValidOtps] = useState(0);
  const [initialAverageOtps, setInitialAverageOtps] = useState(0);

  const formatDateToUTCString = (date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];
  };

  const fetchData = async () => {
    try {
      const response = await axios.post(BILLING_API, {
        start_date: formatDateToUTCString(startDate),
        end_date: formatDateToUTCString(endDate),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching data", error);
      return null;
    }
  };

  const fetchAverageOtps = async () => {
    try {
      const response = await axios.post(AVG_OTPS_API);
      return response.data.estimated_remaining_otps;
    } catch (error) {
      console.error("Error fetching average OTPs", error);
      return null;
    }
  };

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      try {
        const [billingData, avgOtps] = await Promise.all([
          fetchData(),
          fetchAverageOtps(),
        ]);
        setData(billingData);
        setInitialTotalValidOtps(billingData?.total_valid_otps || 0);
        setInitialAverageOtps(avgOtps || 0);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    loadInitial();
  }, []);

  const updateData = async () => {
    setLoading(true);
    try {
      const newData = await fetchData();
      setData(newData);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const teams = useMemo(
    () =>
      Object.values(data || {})
        .filter(
          (team) =>
            team.calculated_otps_cost !== undefined &&
            team.calculated_otps_cost !== 0
        )
        .sort((a, b) => b.calculated_otps_cost - a.calculated_otps_cost),
    [data]
  );

  const teamNames = useMemo(() => teams.map((t) => t.team_name), [teams]);
  const otpsCosts = useMemo(
    () => teams.map((t) => parseFloat(t.calculated_otps_cost.toFixed(2))),
    [teams]
  );

  const chartData = {
    labels: teamNames,
    datasets: [
      {
        label: "Calculated OTP Cost ($)",
        data: otpsCosts,
        backgroundColor: "rgba(56, 189, 248, 0.7)",
        borderColor: "rgba(14, 165, 233, 1)",
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 12 } } },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, font: { size: 11 } },
      },
      y: {
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 } },
      },
    },
  };

  const forecastedOtps = parseFloat(initialAverageOtps + initialTotalValidOtps).toFixed(0);
  const totalPayment = parseFloat(initialTotalValidOtps * 0.01).toFixed(2);
  const forecastedPayment = parseFloat(
    (Number(initialAverageOtps) + initialTotalValidOtps) * 0.01
  ).toFixed(2);

  return (
    <div className="billing-v2">
      <div className="billing-v2-header">
        <h1 className="billing-v2-title">Team-wise OTP Statistics Overview</h1>
        <div className="billing-v2-controls">
          <div className="billing-v2-date-row">
            <DatePicker
              selected={startDate}
              onChange={(d) => setStartDate(d)}
              className="billing-v2-datepicker"
              dateFormat="MM/dd/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={(d) => setEndDate(d)}
              className="billing-v2-datepicker"
              dateFormat="MM/dd/yyyy"
            />
            <button className="billing-v2-apply" onClick={updateData}>
              Apply
            </button>
          </div>
          <select
            className="billing-v2-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
          </select>
        </div>
      </div>

      <div className="billing-v2-cards">
        <div className="billing-v2-stat-card">
          <div className="billing-v2-stat-label">Total Valid OTPs (month to date)</div>
          <div className="billing-v2-stat-value">{initialTotalValidOtps.toLocaleString()}</div>
          <div className="billing-v2-stat-sublabel">Payment: ${totalPayment}</div>
        </div>
        <div className="billing-v2-stat-card accent">
          <div className="billing-v2-stat-label">Forecasted Valid OTPs (full month)</div>
          <div className="billing-v2-stat-value">{Number(forecastedOtps).toLocaleString()}</div>
          <div className="billing-v2-stat-sublabel">Forecasted: ${forecastedPayment}</div>
        </div>
      </div>

      <div className="billing-v2-main">
        <div className="billing-v2-chart-card">
          {loading ? (
            <div className="billing-v2-loading">
              <TailSpin height={48} width={48} color="#38bdf8" ariaLabel="loading" />
            </div>
          ) : (
            <div className="billing-v2-chart-wrapper">
              {chartType === "bar" ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <Line
                  data={{
                    ...chartData,
                    datasets: [
                      {
                        ...chartData.datasets[0],
                        fill: true,
                        tension: 0.3,
                        backgroundColor: "rgba(56, 189, 248, 0.2)",
                        borderColor: "rgba(14, 165, 233, 1)",
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              )}
            </div>
          )}
        </div>

        <div className="billing-v2-table-card">
          <div className="billing-v2-table-wrapper">
            <table className="billing-v2-table">
              <thead>
                <tr>
                  <th>Team Name</th>
                  <th>OTP Count</th>
                  <th>Calculated OTP Cost</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => (
                  <tr key={team.team_name}>
                    <td>{team.team_name}</td>
                    <td>{team.date_range_otps_count}</td>
                    <td className="billing-v2-cost">${parseFloat(team.calculated_otps_cost).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingV2;
